/**
 * NodePromptOverridesPanel — super-admin-only "Advanced: per-node prompt
 * overrides" section inside the IVR tree node-editor drawer.
 *
 * For each prompt key relevant to the node's type (see `nodePromptKeys.ts`),
 * shows:
 *   - The resolved English text from the chain (global → reseller →
 *     customer → node) with a chip explaining which level won.
 *   - An amber "Node override" badge when a node-level row exists.
 *   - An inline editor (3 languages) that writes a node-scoped override via
 *     PUT /api/system-prompts/:id/overrides with ivr_node_id supplied.
 *   - A "Clear node override" button that deletes the row and falls back to
 *     the customer / reseller / global chain.
 *
 * Super-admin only — if the caller isn't super-admin the panel does not
 * mount. The backend also enforces this at the API layer.
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  systemPrompts,
  LANGUAGE_LABELS,
  type PromptLanguage,
  type SystemPromptNodeOverrideRow,
  type SystemPromptRow,
  type SystemPromptTranslationRow,
  type SystemPromptOverrideRow,
} from '@/api/systemPrompts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/cn';
import { ChevronDown, ChevronRight, Pencil, Save, Trash2, Loader2, Play } from 'lucide-react';
import { NODE_PROMPT_KEYS, hasNodePromptOverrides, type NodePromptSlot } from './nodePromptKeys';
import type { IvrNodeType } from '@/api/ivrNodes';

const LANGS: PromptLanguage[] = ['en', 'he', 'yi'];

// ── Utility: group overrides by prompt_key × language ───────────────────────

interface NodeOverrideMap {
  [promptKey: string]: Partial<Record<PromptLanguage, SystemPromptNodeOverrideRow>>;
}

function indexNodeOverrides(rows: SystemPromptNodeOverrideRow[]): NodeOverrideMap {
  const out: NodeOverrideMap = {};
  for (const ov of rows) {
    if (!out[ov.prompt_key]) out[ov.prompt_key] = {};
    out[ov.prompt_key]![ov.language] = ov;
  }
  return out;
}

// ── Inline per-prompt editor ────────────────────────────────────────────────

interface EditorProps {
  slot: NodePromptSlot;
  prompt: SystemPromptRow;
  nodeId: string;
  overrides: Partial<Record<PromptLanguage, SystemPromptNodeOverrideRow>>;
  baselineByLang: Partial<Record<PromptLanguage, ResolvedChainEntry>>;
  onClose: () => void;
}

function InlineEditor({
  slot, prompt, nodeId, overrides, baselineByLang, onClose,
}: EditorProps) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-3 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-slate-700">
          Node override — <span className="font-mono text-[11px]">{slot.key}</span>
        </p>
        <button
          type="button"
          onClick={onClose}
          className="text-[11px] text-slate-500 hover:text-slate-700"
        >
          Close
        </button>
      </div>
      {LANGS.map((lang) => (
        <LangRow
          key={lang}
          prompt={prompt}
          lang={lang}
          nodeId={nodeId}
          override={overrides[lang]}
          baseline={baselineByLang[lang]}
        />
      ))}
      <p className="text-[10px] text-slate-400 leading-snug">
        Audio upload for per-node overrides is coming soon — use text + TTS for
        now. Saved overrides are visible only to this node; the customer /
        reseller / global chain is used when the node override is empty.
      </p>
    </div>
  );
}

interface LangRowProps {
  prompt: SystemPromptRow;
  lang: PromptLanguage;
  nodeId: string;
  override: SystemPromptNodeOverrideRow | undefined;
  baseline: ResolvedChainEntry | undefined;
}

function LangRow({ prompt, lang, nodeId, override, baseline }: LangRowProps) {
  const qc = useQueryClient();
  const [text, setText] = useState(override?.tts_text ?? '');
  const [dirty, setDirty] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: () =>
      systemPrompts.upsertOverride(prompt.id, {
        language: lang,
        tts_text: text.trim() ? text : null,
        ivr_node_id: nodeId,
      }),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['nodePromptOverrides', nodeId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!override) throw new Error('no_override');
      return systemPrompts.deleteOverride(prompt.id, override.id);
    },
    onSuccess: () => {
      setText('');
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['nodePromptOverrides', nodeId] });
    },
  });

  const previewMutation = useMutation({
    mutationFn: () => systemPrompts.preview(prompt.id, lang, text || undefined),
    onSuccess: (data) => {
      setPreviewUrl(data.url);
      setTimeout(() => {
        const audio = new Audio(data.url);
        audio.play().catch(() => undefined);
      }, 80);
    },
  });

  const isRtl = lang === 'he' || lang === 'yi';

  // Build a helpful placeholder — show the baseline text truncated.
  const baselineText = baseline?.ttsText ?? '';
  const placeholder = baselineText
    ? `Falls back to ${sourceLabel(baseline!.source)}: "${truncate(baselineText, 80)}"`
    : 'No fallback text — the caller would hear nothing.';

  return (
    <div className="grid grid-cols-[64px_1fr_auto] gap-2 items-start">
      <div className="pt-2">
        <span className={cn(
          'block text-[11px] font-semibold',
          (lang === 'he' || lang === 'yi') ? 'text-amber-600' : 'text-slate-500',
        )}>
          {LANGUAGE_LABELS[lang]}
        </span>
        {override && (
          <Badge
            className="mt-1 bg-amber-100 text-amber-900 border border-amber-200 text-[9px] font-normal hover:bg-amber-100"
            variant="outline"
          >
            overriding
          </Badge>
        )}
      </div>

      <Textarea
        value={text}
        onChange={(e) => { setText(e.target.value); setDirty(true); }}
        rows={2}
        dir={isRtl ? 'rtl' : 'ltr'}
        className="text-xs resize-none bg-white"
        placeholder={placeholder}
      />

      <div className="flex flex-col gap-1 pt-0.5">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 px-2"
          disabled={!text.trim() || previewMutation.isPending}
          onClick={() => previewMutation.mutate()}
          title="Preview TTS"
        >
          {previewMutation.isPending
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Play className="h-3.5 w-3.5" />}
        </Button>
        <Button
          type="button"
          size="sm"
          className="h-7 px-2"
          disabled={!dirty || saveMutation.isPending}
          onClick={() => saveMutation.mutate()}
          title="Save node override"
        >
          {saveMutation.isPending
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Save className="h-3.5 w-3.5" />}
        </Button>
        {override && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            disabled={deleteMutation.isPending}
            onClick={() => {
              if (confirm(`Clear ${LANGUAGE_LABELS[lang]} node override? The customer / reseller / global chain will apply.`)) {
                deleteMutation.mutate();
              }
            }}
            title="Clear node override"
          >
            {deleteMutation.isPending
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Trash2 className="h-3.5 w-3.5" />}
          </Button>
        )}
      </div>

      {/* audio element is created imperatively in preview click handler to
          avoid stale src issues between renders; previewUrl is kept for
          potential future visual state */}
      {previewUrl && <span className="sr-only">Audio preview ready</span>}
    </div>
  );
}

// ── Resolved-chain entry (client-side best-effort) ──────────────────────────

interface ResolvedChainEntry {
  source: 'node_override' | 'customer_override' | 'reseller_override' | 'global' | 'missing';
  ttsText: string | null;
}

/**
 * Client-side mirror of the backend resolver — used only for showing the
 * "what would the caller hear" preview in the panel. The server remains the
 * source of truth at runtime.
 */
function resolveChain(
  lang: PromptLanguage,
  nodeOverride: SystemPromptNodeOverrideRow | undefined,
  otherOverrides: SystemPromptOverrideRow[],
  translations: SystemPromptTranslationRow[],
): ResolvedChainEntry {
  if (nodeOverride) {
    return { source: 'node_override', ttsText: nodeOverride.tts_text };
  }
  const customer = otherOverrides.find((o) => o.language === lang && o.customer_id && !o.ivr_node_id);
  if (customer) return { source: 'customer_override', ttsText: customer.tts_text };

  const reseller = otherOverrides.find((o) => o.language === lang && o.reseller_id && !o.customer_id && !o.ivr_node_id);
  if (reseller) return { source: 'reseller_override', ttsText: reseller.tts_text };

  const global = translations.find((t) => t.language === lang && t.is_active_tone);
  if (global) return { source: 'global', ttsText: global.tts_text };

  return { source: 'missing', ttsText: null };
}

function sourceLabel(source: ResolvedChainEntry['source']): string {
  switch (source) {
    case 'node_override':     return 'node override';
    case 'customer_override': return 'customer override';
    case 'reseller_override': return 'reseller override';
    case 'global':            return 'global default';
    case 'missing':           return 'no value';
  }
}

function sourceChipClass(source: ResolvedChainEntry['source']): string {
  switch (source) {
    case 'node_override':     return 'bg-amber-100 text-amber-900 border-amber-200';
    case 'customer_override': return 'bg-sky-50 text-sky-800 border-sky-200';
    case 'reseller_override': return 'bg-indigo-50 text-indigo-800 border-indigo-200';
    case 'global':            return 'bg-slate-100 text-slate-700 border-slate-200';
    case 'missing':           return 'bg-red-50 text-red-700 border-red-200';
  }
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

// ── Per-prompt row ──────────────────────────────────────────────────────────

interface PromptRowProps {
  slot: NodePromptSlot;
  nodeId: string;
  prompt: SystemPromptRow | undefined;
  overrides: Partial<Record<PromptLanguage, SystemPromptNodeOverrideRow>>;
  isOpen: boolean;
  onToggle: () => void;
}

function PromptRow({ slot, nodeId, prompt, overrides, isOpen, onToggle }: PromptRowProps) {
  // Full prompt detail (translations + other overrides) — used to compute the
  // fall-back chain preview. Loaded lazily the first time the row opens so
  // we don't hammer the API with one request per prompt on drawer render.
  const { data: detail } = useQuery({
    queryKey: ['systemPrompt', prompt?.id],
    queryFn: () => (prompt ? systemPrompts.get(prompt.id) : Promise.resolve(null)),
    enabled: !!prompt && isOpen,
    staleTime: 30_000,
  });

  const baselineByLang = useMemo(() => {
    const translations = detail?.translations ?? [];
    const other = (detail?.overrides ?? []).filter((o) => !o.ivr_node_id);
    const out: Partial<Record<PromptLanguage, ResolvedChainEntry>> = {};
    for (const lang of LANGS) {
      out[lang] = resolveChain(lang, overrides[lang], other, translations);
    }
    return out;
  }, [detail, overrides]);

  const enResolved: ResolvedChainEntry = baselineByLang.en ?? { source: 'missing', ttsText: null };
  const hasNodeOverride = LANGS.some((l) => overrides[l]);

  return (
    <div className="rounded-md border border-slate-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-3 px-3 py-2 text-left hover:bg-slate-50 transition-colors"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {isOpen
              ? <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              : <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
            <span className="text-[11px] font-semibold text-slate-800">{slot.label}</span>
            <code className="text-[10px] text-slate-400 font-mono">{slot.key}</code>
            {hasNodeOverride && (
              <Badge className="bg-amber-100 text-amber-900 border-amber-200 text-[9px] font-normal hover:bg-amber-100">
                Node override
              </Badge>
            )}
            <span
              className={cn(
                'text-[9px] font-medium border rounded px-1.5 py-0.5',
                sourceChipClass(enResolved.source),
              )}
              title={`English resolves to: ${sourceLabel(enResolved.source)}`}
            >
              {sourceLabel(enResolved.source)}
            </span>
          </div>
          <p className="mt-0.5 ml-5 text-[10px] text-slate-500 leading-snug">{slot.description}</p>
          {enResolved.ttsText && (
            <p className="mt-1 ml-5 text-[11px] text-slate-700 italic line-clamp-2">
              &ldquo;{enResolved.ttsText}&rdquo;
            </p>
          )}
          {!enResolved.ttsText && enResolved.source === 'missing' && (
            <p className="mt-1 ml-5 text-[11px] text-red-600 italic">
              No English text on any level — the caller would hear silence.
            </p>
          )}
        </div>
        <div className="shrink-0 pt-0.5">
          {!isOpen && (
            <span className="inline-flex items-center gap-1 text-[10px] text-indigo-600">
              <Pencil className="h-3 w-3" />
              {hasNodeOverride ? 'Edit' : 'Override'}
            </span>
          )}
        </div>
      </button>

      {isOpen && prompt && (
        <div className="px-3 pb-3">
          <InlineEditor
            slot={slot}
            prompt={prompt}
            nodeId={nodeId}
            overrides={overrides}
            baselineByLang={baselineByLang}
            onClose={onToggle}
          />
        </div>
      )}
      {isOpen && !prompt && (
        <div className="px-3 pb-3">
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            Prompt key <code className="font-mono">{slot.key}</code> is not defined in the system-prompts library yet.
            Create it from the System prompts page first.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Panel (collapsible) ─────────────────────────────────────────────────────

interface Props {
  nodeId: string;
  nodeType: IvrNodeType;
}

export function NodePromptOverridesPanel({ nodeId, nodeType }: Props) {
  const slots = NODE_PROMPT_KEYS[nodeType] ?? [];
  const [expanded, setExpanded] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);

  const { data: promptList } = useQuery({
    queryKey: ['systemPromptsList'],
    queryFn: () => systemPrompts.list(),
    staleTime: 60_000,
    enabled: expanded,
  });

  const { data: nodeOverridesData, isLoading: loadingOverrides } = useQuery({
    queryKey: ['nodePromptOverrides', nodeId],
    queryFn: () => systemPrompts.listForNode(nodeId),
    staleTime: 10_000,
    enabled: expanded,
  });

  const promptByKey = useMemo(() => {
    const out: Record<string, SystemPromptRow> = {};
    for (const p of promptList?.rows ?? []) out[p.prompt_key] = p;
    return out;
  }, [promptList]);

  const overridesByKey = useMemo(
    () => indexNodeOverrides(nodeOverridesData?.rows ?? []),
    [nodeOverridesData],
  );

  const overrideCount = (nodeOverridesData?.rows ?? []).length;

  if (!hasNodePromptOverrides(nodeType)) {
    return (
      <div className="rounded-md bg-slate-50/60 border border-slate-100 px-3 py-2.5 text-xs text-slate-500">
        This node type doesn&apos;t emit any system prompts, so there&apos;s nothing to override here.
        Prompts played directly from customer recordings are edited via the <em>Recordings</em> section above.
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded
            ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
            : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
          <div className="min-w-0">
            <p className="text-[11px] font-semibold text-slate-800">
              Advanced: per-node prompt overrides
            </p>
            <p className="text-[10px] text-slate-500 leading-snug">
              Override platform voice messages for this specific node only. Falls back to
              customer / reseller / global defaults when empty.
            </p>
          </div>
        </div>
        <div className="shrink-0">
          {overrideCount > 0 && (
            <Badge className="bg-amber-100 text-amber-900 border-amber-200 text-[10px] font-semibold hover:bg-amber-100">
              {overrideCount} active
            </Badge>
          )}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50/40 p-3 space-y-2">
          {loadingOverrides && !nodeOverridesData && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading overrides…
            </div>
          )}
          {slots.map((slot) => (
            <PromptRow
              key={slot.key}
              slot={slot}
              nodeId={nodeId}
              prompt={promptByKey[slot.key]}
              overrides={overridesByKey[slot.key] ?? {}}
              isOpen={openKey === slot.key}
              onToggle={() => setOpenKey((k) => (k === slot.key ? null : slot.key))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
